import { useContext } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { useTranslation } from 'react-i18next';
import { Button, Grid, IconButton, Skeleton, SvgIcon, TableHead, Typography, useTheme } from '@mui/material';
import { Plus, Trash01 } from '../../../../../assets';
import { EmptyTable } from '../../../../../components/shared/EmptyTable';
import { RetailerDeliveryLocationModel } from '../../../../../app/services/api/ofdClient';
import { Pill } from '../../../../../components/shared/Pill';
import { UserContext } from '../../../../../components/shared/useUser';

export default function DeliveryLocationTable(props: {
  list: RetailerDeliveryLocationModel[];
  openEditModal: boolean;
  onCloseEditModal: Function;
  onOpenEditModal: Function;
  onOpenDeleteModal: Function;
  isLoading: boolean;
  selectedLocation: RetailerDeliveryLocationModel;
}) {
  const { t } = useTranslation('onboardingWizard');
  const theme = useTheme();
  const { hasPermissionTo } = useContext(UserContext);

  if (props.list?.length == 0) {
    return <EmptyTable
      onAddIsCreating={false}
      withPaginationShown={true}
      text={t('deliveryLocationsTab.title')}
      searchTerm={undefined}
      onClear={() => { }}
      onAdd={() => props.onOpenEditModal()}
      addItemText={t('deliveryLocationsTab.createNewBtn')}
      bodyRowHeight={45}
      headerHeight={55} />;
  }

  return (
    <>
      <Grid container
        sx={{ p: 5 }}
        spacing={4}
        alignItems="center">
        <Grid item
          xs>
          <Typography
            variant='textMD'
            fontWeight={600}
            sx={{ display: 'flex', alignItems: 'center', pl: 3 }}>
            {t('deliveryLocationsTab.title')}
            {props.list?.length > 0 && (
              <Pill
                text={`${props.list.length} ${props.list.length === 1
                  ? t('deliveryLocationsTab.location')
                  : t('deliveryLocationsTab.locations')}`}
              />
            )}
          </Typography>
        </Grid>
        <Grid item
          lg={2}
          sm={2}
          xs={12}
          sx={{ textAlign: 'right' }}>
          <Button
            variant="primary"
            size="md"
            disabled={!hasPermissionTo(['RetailersDetailsLocationAdd'])}
            onClick={() => props.onOpenEditModal()}
            startIcon={<SvgIcon><Plus fill="white" /></SvgIcon>}
            fullWidth
          >
            {t('deliveryLocationsTab.createNewBtn')}
          </Button>
        </Grid>
      </Grid>
      {props.isLoading ? <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
        variant='rectangular'
        height={445.89} /> :
        <TableContainer component={'div'}>
          <Table>
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell>
                  ID
                </TableCell>
                <TableCell width={'25%'}>
                  {t('deliveryLocationsTab.name')}
                </TableCell>
                <TableCell width={'25%'}>
                  {t('deliveryLocationsTab.city')}
                </TableCell>
                <TableCell>
                  {t('deliveryLocationsTab.contactName')}
                </TableCell>
                <TableCell align="right">
                  {t('deliveryLocationsTab.contactNumber')}
                </TableCell>
                <TableCell align="center">
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.list?.map((row, index) => (
                <TableRow key={index}
                  onClick={hasPermissionTo(['RetailersDetailsLocationAdd']) ? () => props.onOpenEditModal(row) : undefined}
                  sx={hasPermissionTo(['RetailersDetailsLocationAdd']) ? { cursor: 'pointer' } : {}}>
                  <TableCell>
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {row.id! < 0 ? 'New' : index + 1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.name}
                  </TableCell>
                  <TableCell>
                    {row.city}
                  </TableCell>
                  <TableCell>
                    {row.contactName}
                  </TableCell>
                  <TableCell align="right">
                    {row.contactNumber}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ p: 0, alignContent: 'center' }}
                  >
                    <IconButton aria-label={t('delete')}
                      disabled={!hasPermissionTo(['RetailersDetailsLocationDelete'])}
                      onClick={(event) => props.onOpenDeleteModal(event, row)}
                      size='small'>
                      <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01></Trash01></SvgIcon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {Array.from({ length: Math.max(0, 10 - (props.list?.length || 0)) }).map((_, index) => (
                <TableRow key={`empty-row-${index}`}
                  sx={{ height: '40px' }}>
                  <TableCell colSpan={6} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
    </>
  );
}


